import { useMemo, useState } from 'react';

import { getOrganizationLogo, ShortedBankName } from '@revenuemarket/bank';
import { Flex } from '@revenuemarket/cds';
import { theme } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { Condition } from '@revenuemarket/react';
import { Money, Typography, Toast } from '@revenuemarket/ui';
import { Button, Descriptions, Divider, Drawer, Input } from 'antd';
import dayjs from 'dayjs';
import Image from 'next/image';

import { Category } from '~apis/cash-flow-overview/getCategories';
import { flattenTree } from '~components/ui/HorizontalTreeTable/Table';
import { useCategories, useCategoriesTree } from '~hooks/queries/categories/query';
import { useGroupLabels } from '~hooks/queries/custom-labels/query';
import {
  useCreateTransactionMemo,
  useTransactionMemos,
  useUpdateTransactionMemo,
} from '~hooks/queries/transactions/memos/query';
import { useBulkLabeling, useTransaction } from '~hooks/queries/transactions/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { AccountType } from '~models/bank/enum';
import { CashFlowType } from '~models/cashFlow';
import { TransactionsMemoModel } from '~models/transactions/memos';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  transactionId: number;
  accountId: number;
  accountType: AccountType;
}

const Memo: React.FC<{
  data: TransactionsMemoModel.Memo;
  transactionId: number;
}> = ({ data, transactionId }) => {
  const { companyId } = useDemoAwareCompanyId();
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(data?.content ?? '');
  const { mutateAsync: updateTransactionMemo } = useUpdateTransactionMemo();
  const { mutateAsync: createTransactionMemo } = useCreateTransactionMemo();

  const onEdit = () => {
    setEditing(true);
  };

  const onSave = async () => {
    setEditing(false);

    if (!data?.id) {
      const response = await createTransactionMemo({
        companyId,
        transactionId: transactionId,
        request: {
          content: content ?? '',
        },
      });

      if (response.Status !== ResponseStatus.SUCCESS) {
        Toast.error('메모 추가에 실패했습니다.');
      }

      return;
    }

    const response = await updateTransactionMemo({
      companyId,
      transactionId: data.transactionId,
      memoId: data.id,
      request: {
        content,
      },
    });

    if (response.Status !== ResponseStatus.SUCCESS) {
      Toast.error('메모 수정에 실패했습니다.');
    }
  };

  return (
    <Flex gap={8} align="center">
      {editing ? (
        <>
          <Input
            size="small"
            bordered={true}
            style={{
              textAlign: 'right',
            }}
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
          <Button size="small" onClick={onSave} type="primary">
            {data ? '수정' : '추가'}
          </Button>
        </>
      ) : (
        <>
          <Typography variant="body1" color={theme.colorTokens.textDefault}>
            {content}
          </Typography>

          <Button size="small" onClick={onEdit}>
            {data ? '편집' : '추가'}
          </Button>
        </>
      )}
    </Flex>
  );
};

const TransactionDetailDrawer = ({
  isOpen,
  onClose,
  transactionId,
  accountId,
  accountType,
}: Props) => {
  const { companyId } = useDemoAwareCompanyId();

  const { data: memosData } = useTransactionMemos({ companyId, transactionId });
  const memos = memosData?.Data?.data || [];

  const { data } = useTransaction({ companyId, transactionId, accountType });
  const detail = data?.Data?.data;

  const transactionFlowType =
    (detail?.transactionOutAmount ?? 0) > 0 ? CashFlowType.OUT : CashFlowType.IN;
  const { data: categoriesData } = useCategories({ companyId });
  const categories =
    categoriesData?.Data?.data?.categories.filter((category) => {
      return category.cashFlowType === transactionFlowType;
    }) ?? [];

  const { data: categoriesTreeData } = useCategoriesTree({ companyId });
  const categoriesKeyedByCategoryId = useMemo(
    () =>
      categories.reduce<Record<string, Category>>(
        (acc, cur) => ({
          ...acc,
          [cur.categoryLabelKey]: {
            ...cur,
            companyDetailedCategoryId:
              cur.categoryLabelKey.split('_')[1] === 'null'
                ? undefined
                : Number(cur.categoryLabelKey.split('_')[1]),
          },
        }),
        {},
      ),
    [categories],
  );

  const { data: customLabelsData } = useGroupLabels({ companyId });
  const customLabelOptions = useMemo(
    () =>
      customLabelsData?.Data?.data?.map((customLabel) => ({
        label: customLabel.name,
        value: customLabel.id,
      })),
    [customLabelsData],
  );

  const { mutateAsync: patchBulkLabelling } = useBulkLabeling();

  const categoriesTree = categoriesTreeData?.Data?.data?.tree?.children;
  const flattenedTree = flattenTree(categoriesTree ?? []).filter(
    (item) => !!item.value?.name && item.value?.cashFlowType === transactionFlowType,
  );
  const categoryLabelKey =
    detail?.labels[0]?.categoryId + '_' + detail?.labels[0]?.companyDetailedCategoryId;
  const targetValue = useMemo(() => {
    if (!detail) return null;

    return flattenedTree.find((item) => item.value?.categoryLabelKey === categoryLabelKey);
  }, [flattenedTree, categoryLabelKey]);

  if (detail == null) return null;

  const transaction = {
    ...detail,
    accountId: detail.bankAccountId,
    companyId,
    accountType: detail.bankAccountType,
    accountNumber: detail.displayAccountNumber?.replace(/-/g, '') ?? '',
    hasMapping: detail.labels?.some((label) => label?.mappingLabel != null) ?? false,
    mappingLabel: detail.labels?.find((label) => label?.mappingLabel != null)?.mappingLabel,
    labelId: detail?.labels[0]?.labelId ?? null,
    categoryId: detail?.labels[0]?.categoryId ?? null,
    businessEntityId: detail?.labels[0]?.businessEntityId ?? null,
    companyDetailedCategoryId: detail?.labels[0]?.companyDetailedCategoryId ?? null,
    customLabelId: detail?.labels[0]?.groupLabelId ?? null,
    labelKey: `${detail?.labels[0]?.categoryId}_${
      detail?.labels[0]?.companyDetailedCategoryId ?? null
    }`,
    allLabelIds: detail.labels.map((label) => label.labelId),
  };

  const targetLabels =
    detail.transactionInAmount > 0
      ? [...detail.labels].sort((a, b) => b.amount - a.amount)
      : [...detail.labels].sort((a, b) => a.amount - b.amount);

  const targetCategory = targetLabels.find((l) => l.categoryId != null);

  const isDeposit = detail.transactionInAmount > 0;
  const amount = isDeposit ? detail.transactionInAmount : detail.transactionOutAmount;
  const krwAmount = isDeposit ? detail.transactionInKrwAmount : detail.transactionOutKrwAmount;

  return (
    <Drawer open={isOpen} onClose={onClose} title="상세 내역">
      <Flex direction="column">
        <Flex gap={8} align="center">
          <Image
            width={16}
            height={16}
            src={getOrganizationLogo(detail.bankCode).x2}
            alt={detail.bankAccountAliasName}
          />
          <Typography variant="body1" color={theme.colorTokens.textDescription}>
            {detail.bankAccountAliasName ||
              `${ShortedBankName[detail.bankCode]} ${transaction.accountNumber.slice(-4)}`}
          </Typography>
        </Flex>
        <Flex gap={8} align="center">
          <Typography variant="h1" color={theme.colorTokens.textDefault}>
            <Condition expression={isDeposit} then={() => '+'} else={() => '-'} />
            <Money amount={krwAmount} />원
          </Typography>
          <Condition
            expression={detail.currencyCode !== 'KRW'}
            then={() => (
              <Typography variant="caption1" color={theme.colorTokens.textDescription}>
                <Condition expression={isDeposit} then={() => '+'} else={() => '-'} />
                <Money amount={amount} currency={detail.currencyCode} />
              </Typography>
            )}
          />
        </Flex>
        <Divider />
        <Descriptions
          column={1}
          bordered={false}
          colon={false}
          labelStyle={{
            width: '100px',
            height: '54px',
            alignItems: 'center',
          }}
          contentStyle={{
            height: '54px',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <Descriptions.Item label="적요">
            <Typography variant="body1" color={theme.colorTokens.textDefault}>
              {detail.transactionDescription}
            </Typography>
          </Descriptions.Item>
          <Descriptions.Item label="거래자명">
            <Typography variant="body1" color={theme.colorTokens.textDefault}>
              {detail.transactionName || '-'}
            </Typography>
          </Descriptions.Item>
          <Descriptions.Item label="거래유형">
            <Typography variant="body1" color={theme.colorTokens.textDefault}>
              {detail.transactionType || '-'}
            </Typography>
          </Descriptions.Item>
          <Descriptions.Item label="계좌번호">
            <Typography variant="body1" color={theme.colorTokens.textDefault}>
              {ShortedBankName[detail.bankCode]} {detail.displayAccountNumber ?? ''}
            </Typography>
          </Descriptions.Item>
          <Descriptions.Item label="계좌별칭">
            <Typography variant="body1" color={theme.colorTokens.textDefault}>
              {detail.bankAccountAliasName || '-'}
            </Typography>
          </Descriptions.Item>
          <Descriptions.Item label="거래지점">
            <Typography variant="body1" color={theme.colorTokens.textDefault}>
              {detail.transactionBranch || '-'}
            </Typography>
          </Descriptions.Item>
          <Descriptions.Item label="일시">
            <Typography variant="body1" color={theme.colorTokens.textDefault}>
              {dayjs(detail.transactionAt).format('YYYY년 MM월 DD일 HH:mm:ss')}
            </Typography>
          </Descriptions.Item>
          <Descriptions.Item label="거래 후 잔액">
            <Flex direction="column">
              <Typography variant="body1" color={theme.colorTokens.textDefault}>
                <Money amount={detail.transactionAfterBalanceKrw} />원
              </Typography>
              <Condition
                expression={detail.currencyCode !== 'KRW'}
                then={() => (
                  <Typography variant="caption1" color={theme.colorTokens.textDescription}>
                    <Condition expression={isDeposit} then={() => <>+</>} else={() => <>-</>} />
                    <Money amount={detail.transactionAfterBalance} currency={detail.currencyCode} />
                  </Typography>
                )}
              />
            </Flex>
          </Descriptions.Item>

          <Descriptions.Item label="메모">
            <Memo data={memos[0]} transactionId={detail.transactionId} />
          </Descriptions.Item>
        </Descriptions>
      </Flex>
    </Drawer>
  );
};

export default TransactionDetailDrawer;
